import {
  Component,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import $ from 'jquery';
import { SubSink } from 'subsink';
import { SharedModule } from '@Shared/shared.module';
import { AppService } from 'app/app.service';
import { TeamsService, UserAdminService } from '@Services';
import { FormArray, FormGroup, UntypedFormBuilder, } from '@angular/forms';
import { TableColumnDefinition } from 'app/shared/components/mrs-table/models/table-models';
import { TeamCalendarComponent } from '../team-calendar/team-calendar.component';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { EditUsersV2Component } from '@App/manage-users/edit-users-v2/edit-users-v2.component';
import { areDatesEquivalent } from '@Utils/time-utils';

interface TeamMemberSchedule {
  endTime: Date;
  formattedEndTime: Date;
  formattedStartTime: Date;
  id: string;
  isActive: boolean;
  startTime: Date;
  userId: string;
}

interface TeamMemberSchedules {
  schedules: TeamMemberSchedule[];
  userName: string;
  userId: string;
  userAvailable: boolean;
}

@Component({
  selector: 'manage-team-members-schedule',
  templateUrl: './team-members-schedule.component.html',
  styleUrls: ['./team-members-schedule.component.scss', '../teams/app-teams.component.scss'],
  imports: [SharedModule, TeamCalendarComponent]
})
export class TeamMembersScheduleComponent implements OnInit, OnDestroy, OnChanges {
  private readonly subs = new SubSink();
  canRead = false;
  @ViewChild(MatSort) sort: MatSort;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @Input() id: string;
  @Input() selectedDate: Date;
  @Input() setSelectedDate!: (day: Date) => void;

  teamMembersUnAvailableListScheduleDataSource = new MatTableDataSource<any>();
  teamMembersAvailableScheduleDataSource = new MatTableDataSource<any>();
  displayedColumnsDef: string[];
  scheduleForm!: FormGroup;
  displayedColumns: TableColumnDefinition[];
  initialSort = 'name';
  userProfileV2: boolean;
  teamMemberSchedules: TeamMemberSchedules[];
  accounts: any[];

  editUsersV2DialogRef: MatDialogRef<EditUsersV2Component>;

  constructor(
    private readonly appService: AppService,
    private readonly teamsService: TeamsService,
    private readonly userAdminService: UserAdminService,
    private readonly _fb: UntypedFormBuilder,
    public dialog: MatDialog
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.selectedDate?.currentValue !== changes.selectedDate?.previousValue && changes.selectedDate?.currentValue !== null) {
      this.setData();
    }
  }

  ngOnInit() {
    $('#sidenav').removeClass('hidenav');
    this.canRead = this.appService.checkACL('User Management', 'R');
    this.scheduleForm = this._fb.group({
      schedules: this._fb.array([])
    });
    this.displayedColumns = [
        {
            def: 'userName',
            header: 'Name & Email',
            sortFields: ['userName'],
            columnStyles: {
                'width': '70%',
            },
            rowDefs: [
                {
                    className: 'name',
                    dataName: 'userName',
                }
            ]
        }
    ];
    this.setupClients();
    this.getTeamMemberSchedules();
  }

  setupClients() {
    this.appService.getData('Accounts').subscribe((response) => {
        this.accounts = response.data;
    });
  }

  getTeamMemberSchedules() {
    this.subs.add(this.teamsService.getTeamMemberSchedules(this.id).subscribe(data => {
      this.teamMemberSchedules = data;
      for (const sc of data) {
        const itemsArray = this._fb.array(
          sc.schedules.map((item: any) => this._fb.group(item))
        );

        const scheduleGroup = this._fb.group({
          userId: [sc.userId],
          userAvailable: sc.userAvailable,
          schedules: itemsArray
        });

        this.schedules.push(scheduleGroup);
      }
      this.setData();
    }));
  }

  isSelectedDateToday(): boolean {
    const now = new Date();
    return (
      this.selectedDate.getUTCFullYear() === now.getUTCFullYear() &&
      this.selectedDate.getUTCMonth() === now.getUTCMonth() &&
      this.selectedDate.getUTCDate() === now.getUTCDate()
    );
  }

  getUserAvailabilityClass(row: any): string {
    if (this.isSelectedDateToday()) {
      if (row.userAvailable) {
        return 'available-state';
      }
      else if (this.isScheduledUnavailableNow(row.schedules)) {
        return 'scheduled-off-state';
      }
      else {
        return 'toggled-off-state';
      }
    }
    
    if (row['formattedSchedules'] !== '') {
      return 'scheduled-off-state';
    }
    else if (!row.userAvailable) {
      return 'toggled-off-state';
    }
    else {
      return 'available-state';
    }
  }

  isScheduledUnavailableNow(schedules: any[]): boolean {
    if (!schedules || schedules.length === 0) return false;
    const now = new Date();
    for (const schedule of schedules) {
      const start = new Date(schedule.formattedStartTime);
      const end = new Date(schedule.formattedEndTime);
      if (now >= start && now <= end)
        return true;
    }
    return false;
  }

  setData() {
		const formattedTeamMemberUnAvailableListSchedules = [];
		const formattedTeamMemberAvailableSchedules = [];
    if (this.teamMemberSchedules) {
      for (const tm of this.teamMemberSchedules) {
        const formattedSchedules = this.getFormattedSchedulesForToday(tm.userAvailable, tm.schedules);
        if ((tm.userAvailable && this.isSelectedDateToday()) || (formattedSchedules.length === 0 && tm.userAvailable)) {
          formattedTeamMemberAvailableSchedules.push({
            ...tm,
            earliestStartTime: this.getEarliestStartTime(tm.schedules),
            formattedSchedules
          });
        }
        else {
          formattedTeamMemberUnAvailableListSchedules.push({
            ...tm,
            earliestStartTime: this.getEarliestStartTime(tm.schedules),
            formattedSchedules
          });
        }
      }
    }
    const sortedUnavailableListSchedules = [...formattedTeamMemberUnAvailableListSchedules].sort((a, b) => this.sortFormattedTeamMemberSchedules(a, b));
    this.teamMembersUnAvailableListScheduleDataSource.data = sortedUnavailableListSchedules;
    const sortedAvailableSchedules = [...formattedTeamMemberAvailableSchedules].sort((a, b) => this.sortFormattedTeamMemberSchedules(a, b));
    this.teamMembersAvailableScheduleDataSource.data = sortedAvailableSchedules;
    this.displayedColumnsDef = this.displayedColumns?.map(x => x.def);
  }

  sortFormattedTeamMemberSchedules(a, b) {
    const aHasSchedules = a.formattedSchedules.length > 0;
    const bHasSchedules = b.formattedSchedules.length > 0;

    if (aHasSchedules && !bHasSchedules) return -1;
    if (!aHasSchedules && bHasSchedules) return 1;

    const aEarliest = this.getEarliestStartTime(a.schedules);
    const bEarliest = this.getEarliestStartTime(b.schedules);

    if (aEarliest && bEarliest) {
      const diff = aEarliest.getTime() - bEarliest.getTime();
      if (diff !== 0) 
        return diff;
    }

    if (!a.userAvailable && b.userAvailable) return -1;
    if (a.userAvailable && !b.userAvailable) return 1;

    return a.userName.localeCompare(b.userName);
  }

  private getEarliestStartTime(schedules: any[]): Date | null {
    const matches = schedules.filter(s => {
      const start = new Date(s.startTime);
      return (
        start.getUTCFullYear() === this.selectedDate.getUTCFullYear() &&
        start.getUTCMonth() === this.selectedDate.getUTCMonth() &&
        start.getUTCDate() === this.selectedDate.getUTCDate()
      );
    });

    if (!matches.length) return null;

    return new Date(
      Math.min(...matches.map(s => new Date(s.startTime).getTime()))
    );
  }

  getFormattedSchedulesForToday(userAvailable: boolean, schedules: any[]): string {
    const sortedSchedules = [...schedules].sort((a, b) => {
      const startA = new Date(a.startTime).getTime();
      const startB = new Date(b.startTime).getTime();
      return startA - startB;
    });

    const startOfSelectedDate = new Date(Date.UTC(this.selectedDate.getUTCFullYear(), this.selectedDate.getUTCMonth(), this.selectedDate.getUTCDate(), 0, 0, 0));
    const endOfSelectedDate = new Date(Date.UTC(this.selectedDate.getUTCFullYear(), this.selectedDate.getUTCMonth(), this.selectedDate.getUTCDate(), 23, 59, 59));

    const matches = sortedSchedules.filter(s => {
      const start = new Date(s.startTime);
      const end = new Date(s.endTime);

      // schedule starts on selected date
      const startsToday =
        start.getUTCFullYear() === this.selectedDate.getUTCFullYear() &&
        start.getUTCMonth() === this.selectedDate.getUTCMonth() &&
        start.getUTCDate() === this.selectedDate.getUTCDate();

      // schedule ends on selected date
      const endsToday =
        end.getUTCFullYear() === this.selectedDate.getUTCFullYear() &&
        end.getUTCMonth() === this.selectedDate.getUTCMonth() &&
        end.getUTCDate() === this.selectedDate.getUTCDate() &&
        !areDatesEquivalent(startOfSelectedDate, end)

      // selected date is within a scheduled range of unavailability
      const includesToday = end >= endOfSelectedDate && start <= startOfSelectedDate;

      return startsToday || endsToday || includesToday;
    });

    if (!matches.length)
      return '';

    return matches
      .map(s => {
        const start = new Date(s.startTime);
        const end = new Date(s.endTime);
        const includesToday = end >= endOfSelectedDate && start <= startOfSelectedDate;

        if (includesToday) {
          return '12:00 AM - 11:59 PM';
        }
        else if (start < startOfSelectedDate) {
          return `12:00 AM - ${this.formatTime(s.formattedEndTime)}`;
        }
        else if (end > endOfSelectedDate) {
          return `${this.formatTime(s.formattedStartTime)} - 11:59 PM`;
        }

        return `${this.formatTime(s.formattedStartTime)} - ${this.formatTime(s.formattedEndTime)}`;
      })
      .join(', ');
  }

  formatTime(iso: string): string {
    return new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true });
  }

  isScheduleForToday(startTime: string): boolean {
    const dtStartTime = new Date(startTime);
    const result = 
        dtStartTime.getUTCFullYear() === this.selectedDate.getUTCFullYear() &&
        dtStartTime.getUTCMonth() === this.selectedDate.getUTCMonth() &&
        dtStartTime.getUTCDate() === this.selectedDate.getUTCDate();
    return result;
  }

  onRowClicked(row) {
    this.subs.add(this.userAdminService.getUser(row.userId).subscribe(result => {
      this.editUsersV2DialogRef = this.dialog.open(EditUsersV2Component, {
          minWidth: '1000px',
          width: '50%',
          minHeight: 'min(820px, 90vh)',
          height: '80%',
          disableClose: true,
          data: {
              user: result,
              accounts: this.accounts,
              isReadOnly: false
          }
      });
    }));
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  get schedules(): FormArray {
    return this.scheduleForm.get('schedules') as FormArray;
  }
}
